'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface EmailAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
  defaultMode?: 'login' | 'register';
}

export default function EmailAuthModal({
  isOpen,
  onClose,
  onSuccess,
  title = 'Sign In',
  description,
  defaultMode = 'login',
}: EmailAuthModalProps) {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setMode(defaultMode);
      setEmail('');
      setPassword('');
      setDisplayName('');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, defaultMode]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (mode === 'login') {
        await signIn(email, password);
        setSuccess('Signed in successfully!');
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 800);
      } else {
        if (!displayName.trim()) {
          setError('Please enter your display name.');
          setLoading(false);
          return;
        }
        await signUp(email, password, { fullName: displayName.trim() });
        setSuccess('Account created! You are now signed in.');
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 1000);
      }
    } catch (err: any) {
      const msg = err?.message || 'Something went wrong. Please try again.';
      if (msg.includes('Invalid login credentials')) {
        setError('Incorrect email or password.');
      } else if (msg.includes('User already registered')) {
        setError('An account with this email already exists. Please sign in.');
      } else if (msg.includes('Password should be at least')) {
        setError('Password must be at least 6 characters.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-secondary border border-stroke-primary rounded-lg shadow-2xl max-w-sm w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-brand-red"></div>
            <h2 className="text-lg font-bold text-text-primary uppercase tracking-wide">
              {mode === 'login' ? title : 'Create Account'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors p-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-5">
          {description && (
            <p className="text-text-secondary text-sm mb-4">{description}</p>
          )}

          {/* Mode toggle */}
          <div className="flex border border-stroke-primary rounded-[4px] mb-5 overflow-hidden">
            <button
              onClick={() => { setMode('login'); setError(null); setSuccess(null); }}
              className={`flex-1 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${
                mode === 'login' ? 'bg-brand-red text-white' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setMode('register'); setError(null); setSuccess(null); }}
              className={`flex-1 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${
                mode === 'register' ? 'bg-brand-red text-white' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-wide mb-1.5">
                  Display Name <span className="text-brand-red">*</span>
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  maxLength={60}
                  required
                  className="input-field"
                />
              </div>
            )}

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-wide mb-1.5">
                Email <span className="text-brand-red">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-wide mb-1.5">
                Password <span className="text-brand-red">*</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'register' ? 'Min. 6 characters' : '••••••••'}
                minLength={6}
                required
                className="input-field"
              />
            </div>

            {error && (
              <div className="bg-red-500 bg-opacity-10 border border-red-500 border-opacity-30 rounded-[4px] px-3 py-2.5">
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-500 bg-opacity-10 border border-green-500 border-opacity-30 rounded-[4px] px-3 py-2.5">
                <p className="text-green-500 text-sm">{success}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2 justify-center">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                mode === 'login' ? 'Sign In with Email' : 'Create Account'
              )}
            </button>
          </form>

          <p className="text-text-muted text-xs text-center mt-4">
            {mode === 'login' ? (
              <>
                No account?{' '}
                <button
                  onClick={() => { setMode('register'); setError(null); }}
                  className="text-brand-red hover:underline font-bold"
                >
                  Register free
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => { setMode('login'); setError(null); }}
                  className="text-brand-red hover:underline font-bold"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
